/**
 * Join requests admin page — list pending chapter join requests and
 * approve / reject them.
 *
 * Route: /admin/join-requests
 *
 * Branch-level requests are not surfaced here while branches are hidden
 * from the admin UI. The branch entity and API still exist; restoring
 * branch UI here is a matter of re-rendering the second table.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ApiClientError,
  chaptersApi,
  chapterJoinRequestsApi,
  type ChapterJoinRequestRow,
  type ChapterSummary,
} from '@/services/api';

type FilterState = 'pending' | 'approved' | 'rejected';

export function BranchJoinRequestsPage() {
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [chapterId, setChapterId] = useState<string>('');
  const [filter, setFilter] = useState<FilterState>('pending');

  const [chapterRequests, setChapterRequests] = useState<ChapterJoinRequestRow[]>([]);
  const [chapterRequestsError, setChapterRequestsError] = useState<string | null>(null);
  const [chapterRequestBusyId, setChapterRequestBusyId] = useState<string | null>(null);

  const loadChapterRequests = useCallback(async () => {
    if (!chapterId) {
      setChapterRequests([]);
      return;
    }
    setChapterRequestsError(null);
    try {
      const rows = await chapterJoinRequestsApi.list(chapterId, filter);
      setChapterRequests(rows);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 403) {
        setChapterRequestsError('You do not have admin access for this chapter.');
      } else {
        setChapterRequestsError(err instanceof Error ? err.message : 'Failed to load chapter requests');
      }
      setChapterRequests([]);
    }
  }, [chapterId, filter]);

  useEffect(() => {
    void loadChapterRequests();
  }, [loadChapterRequests]);

  async function handleChapterDecision(id: string, action: 'approve' | 'reject') {
    if (!chapterId) return;
    setChapterRequestBusyId(id);
    try {
      if (action === 'approve') await chapterJoinRequestsApi.approve(chapterId, id);
      else await chapterJoinRequestsApi.reject(chapterId, id);
      await loadChapterRequests();
    } catch (err) {
      setChapterRequestsError(err instanceof Error ? err.message : `Failed to ${action}`);
    } finally {
      setChapterRequestBusyId(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    chaptersApi
      .list('navarasam')
      .then((rows) => {
        if (cancelled) return;
        setChapters(rows);
        if (rows.length > 0 && !chapterId) setChapterId(rows[0]!.id);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold text-white">Join requests</h1>
        <p className="text-white/60 mt-1">Approve or reject members requesting to join a chapter.</p>
      </div>

      <section className="card">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Chapter</label>
            <select value={chapterId} onChange={(e) => setChapterId(e.target.value)} className="input">
              {chapters.map((c) => (
                <option key={c.id} value={c.id}>{c.displayName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Filter</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value as FilterState)} className="input">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </section>

      <section className="card">
        <h2 className="text-xl font-semibold text-white mb-3">
          {filter === 'pending' ? 'Pending requests' : `${filter.charAt(0).toUpperCase() + filter.slice(1)} requests`}
          {chapterRequests.length > 0 && (
            <span className="text-sm text-yellow-400 ml-2">({chapterRequests.length})</span>
          )}
        </h2>
        {chapterRequestsError ? (
          <p className="text-red-400">{chapterRequestsError}</p>
        ) : chapterRequests.length === 0 ? (
          <p className="text-white/60 text-sm">No {filter} requests.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-white/60 uppercase text-xs">
                <tr>
                  <th className="px-2 py-2">User</th>
                  <th className="px-2 py-2">Email</th>
                  <th className="px-2 py-2">Note</th>
                  <th className="px-2 py-2">Submitted</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="text-white/80">
                {chapterRequests.map((r) => (
                  <tr key={r.id} className="border-t border-white/10">
                    <td className="px-2 py-2">{r.user?.displayName ?? (r.userId ? r.userId.slice(0, 8) + '…' : '—')}</td>
                    <td className="px-2 py-2">{r.user?.email ?? '—'}</td>
                    <td className="px-2 py-2 text-white/60 max-w-xs truncate">{r.requestNote ?? '—'}</td>
                    <td className="px-2 py-2">{new Date(r.createdAt).toLocaleString()}</td>
                    <td className="px-2 py-2 text-right">
                      {filter === 'pending' && (
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            disabled={chapterRequestBusyId === r.id}
                            onClick={() => handleChapterDecision(r.id, 'approve')}
                            className="text-xs px-3 py-1 bg-green-500/20 text-green-300 border border-green-500/30 rounded hover:bg-green-500/30"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={chapterRequestBusyId === r.id}
                            onClick={() => handleChapterDecision(r.id, 'reject')}
                            className="text-xs px-3 py-1 bg-red-500/20 text-red-300 border border-red-500/30 rounded hover:bg-red-500/30"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default BranchJoinRequestsPage;
